import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {EMPTY, Observable, of, throwError} from "rxjs";
import {finalize, map, switchMap} from "rxjs/operators";
import {AppComponent} from "../app.component";
import {Batiment} from "../Models/Batiment";
import {User} from "../Models/User";
import {Agency} from "../Models/Agency";
import {Adresse} from "../Models/Adresse";
import {EmailRegex, PhoneFrRegex} from "../const/utils";
import {ROUTES_PATHS} from "../const/routes";
import {UserTypeEnum} from "../enums/user-type.enum";
import {AnalyticsService} from "../services/analytics.service";
import {getEstimationFromId} from "../helpers/estimation.helper";
import {settings} from "../const/settings";
import {SiteKeyEnum} from "../enums/site-key.enum";
import {ContactV2} from "../Models/ContactV2";
import {ApiV2ErrorCodes, hasApiV2ErrorCode} from "../const/api-v2-error-codes";

/** Choix de parcours proposés à la fin du formulaire. */
export enum ContactExterneChoice {
	CONTACT_EMAIL = 'contact_email',
	PAYER_ESTIMATION = 'payer_estimation'
}

/** ACTE_ID "Être mis en relation avec un pro" (Actes.Partners in the legacy API). */
const ACTE_ID_PARTNERS = 2;

/**
 * Formulaire de contact des référentiels KTV_EXTERNAL.
 * Même fonctionnement que app-form-inscription jusqu'au submit, puis suit le parcours décrit dans KTV-55 :
 *  - "Demande de contact par email" : redirection vers la page partenaires ;
 *  - "Payer l'estimation" : recherche / création de l'utilisateur -> connexion
 *    -> rattachement de la simulation -> paiement Stripe ou décompte d'une estimation -> estimation rapide.
 */
@Component({
	selector: 'app-form-contact-externe',
	templateUrl: './form-contact-externe.component.html',
	encapsulation: ViewEncapsulation.None,
	styleUrls: ['./form-contact-externe.component.less']
})
export class FormContactExterneComponent implements OnInit {
	textesForm: any = "";
	globalTxt: any = "";
	modalsTxt: any = "";
	submitted = false;
	loading = false;
	userForm!: FormGroup;
	userRole: any = localStorage.getItem('user_role');
	simUuid: any = localStorage.getItem('simUuid');
	form_created: boolean = false;
	showMessageEmailOtherReferential: boolean = false;
	batiment: Batiment = JSON.parse(localStorage.getItem("BATIMENT")!);

	public readonly ContactExterneChoice = ContactExterneChoice;

	constructor(
		public app: AppComponent,
		private formBuilder: FormBuilder,
		public analyticsService: AnalyticsService
	) {
	}

	get f() {
		return this.userForm.controls;
	}

	ngOnInit(): void {
		this.getInfosInit("txtFormContactExterne");
	}

	getInfosInit(_nameJson: string) {
		// Get the global texts
		this.app.dataInitService.getGlobalTextsOnce(globalTxt => {
			this.globalTxt = globalTxt;

			// Get the modal texts (error messages)
			this.app.dataInitService.getModalTextsOnce(modalsTxt => this.modalsTxt = modalsTxt);

			// Get the form texts
			this.app.sharedToolsService.loadTexts(_nameJson)
				.subscribe(textes => {
					this.textesForm = textes;
					this.doUserForm();
				});
		});
	}

	doUserForm() {
		this.userForm = this.formBuilder.group({
			USR_NOM: ['', [Validators.required]],
			USR_PRENOM: ['', [Validators.required]],
			USR_EMAIL: ['', [Validators.required, Validators.pattern(EmailRegex)]],
			USR_TEL_MOBILE: ['', [Validators.required, Validators.pattern(PhoneFrRegex)]],
			cguOk: [false, [Validators.requiredTrue]]
		});

		this.form_created = true;
	}

	//============================================================= Submit
	onSubmit(choice: ContactExterneChoice) {
		this.showMessageEmailOtherReferential = false;
		this.submitted = true;

		if (this.userForm.invalid || this.loading) return;

		if (choice === ContactExterneChoice.CONTACT_EMAIL) {
			this.analyticsService.trackEvent('user_interaction', 'click_mise_en_relation', 'value : Mise en relation entreprises');
			this.goToPartners();
		} else {
			this.analyticsService.trackEvent('user_interaction', 'click_estimation_payante', 'value : Téléchargement estimation payante');
			this.payEstimation();
		}
	}

	//============================================================= Parcours "Demande de contact par email"
	/**
	 * Same as app-form-inscription (acteId 2): POST UsrCtrl/Inscription with ACTE_ID = Partners saves the contact
	 * on the simulation (SIN_ASSURE_NOM / _PRENOM / _EMAIL / _MOBILE), used by the partners email (POST /SendMailPartner).
	 * The sponsor is the generic account, found by its guid (USR_NUMERO_EXTERNE).
	 */
	private goToPartners() {
		const user = this.buildUser();
		user.ACTE_ID = ACTE_ID_PARTNERS;
		user.USR_NUMERO_EXTERNE = JSON.parse(localStorage.getItem("token")!)?.guid ?? JSON.parse(localStorage.getItem("guid")!) ?? '';

		this.loading = true;

		this.app.apiToolsService.inscription(user).pipe(
			finalize(() => this.loading = false)
		).subscribe({
			next: () => this.app.router.navigate(['/' + ROUTES_PATHS.PARTENAIRES]),
			error: (err: any) => {
				console.error("inscription (partners) error ==> ", err);
				this.openModalError(this.modalsTxt.modalErrorApi);
			}
		});
	}

	//============================================================= Parcours "Payer l'estimation"
	private payEstimation() {
		const contact = this.buildContactV2();
		let isNewUser = false;

		this.loading = true;

		// Look for the user (API v2, generic account token), create it if it does not exist
		this.app.apiToolsService.getUserByEmail(contact.email).pipe(
			switchMap((existingUser: any) => {
				if (existingUser) return of(existingUser);

				isNewUser = true;
				return this.app.apiToolsService.createUser(contact);
			}),
			// Sign in with the user account (legacy API), the response also holds the user's v2 token
			switchMap((dbUser: any) => {
				// Without a guid, the legacy SignIn falls back to login/password and could sign in another user
				const guid = dbUser?.externNumber;
				if (!guid) return throwError(new Error("User without externNumber: " + JSON.stringify(dbUser)));

				return this.app.apiToolsService.loginUsrByGuid(guid).pipe(
					map((loginResp: any) => {
						if (loginResp?.guid !== guid) throw new Error("Signed in user (" + loginResp?.guid + ") is not the expected one (" + guid + ")");
						return loginResp;
					})
				);
			}),
			switchMap((loginResp: any) => {
				localStorage.setItem('token', JSON.stringify(loginResp));
				localStorage.setItem('guid', JSON.stringify(loginResp?.guid));

				// Attach the simulation to the user now signed in, and save the contact in SIN_ASSURE_*
				return this.app.apiToolsService.changeSimulationUser(this.getSimUuid(), contact).pipe(map(() => loginResp));
			}),
			switchMap((loginResp: any) => this.handleRemainingEstimations(loginResp, isNewUser)),
			finalize(() => this.loading = false)
		).subscribe({
			error: (err: any) => this.handlePayEstimationError(err)
		});
	}

	/**
	 * nbEstimationRestantes <= 0 (outOfSimu) :
	 *  - new user -> Stripe checkout (payment status and "simulation-used" are handled on return from Stripe) ;
	 *  - otherwise -> products page.
	 * nbEstimationRestantes > 0 -> one estimation is used, then redirection to the quick estimation page.
	 */
	private handleRemainingEstimations(loginResp: any, isNewUser: boolean): Observable<void> {
		if (loginResp?.outOfSimu) {
			if (isNewUser) {
				// KTV_EXTERNAL uses the SeLoger single-estimation product
				return this.app.apiToolsService.getProductByRefId(settings[SiteKeyEnum.SELOGER].ref_id!).pipe(
					map((product: any) => {
						this.app.sharedToolsService.goToStripe(product.ProductId, product.PriceId);
					})
				);
			}

			this.app.confirmDialogService.confirmThis('', this.textesForm.texteMsgOutOfSimu, "alert", "",
				() => this.app.router.navigate(['/' + ROUTES_PATHS.PRODUITS]),
				() => {
				});
			return EMPTY;
		}

		return this.app.apiToolsService.trialSimulationUsed(this.simUuid?.replace(/"/g, '')).pipe(
			switchMap(() => getEstimationFromId(parseInt(this.batiment?.simulationId!), this.app.apiToolsService)),
			map(() => {
				this.app.router.navigate(['/' + ROUTES_PATHS.ESTIMATION_RAPIDE]);
			})
		);
	}

	private handlePayEstimationError(err: any) {
		console.error("payEstimation error ==> ", err);

		// User already exists in another referential not allowed for KTV_EXTERNAL
		if (hasApiV2ErrorCode(err, ApiV2ErrorCodes.USER_EXISTS_IN_ANOTHER_REFERENCE)) {
			this.showMessageEmailOtherReferential = true;
			return;
		}

		this.openModalError(this.modalsTxt.modalErrorApi);
	}

	//============================================================= Private functions
	private buildUser(): User {
		const user = new User();
		user.USR_ORG = new Agency();
		user.USR_ADR = new Adresse();
		user.USR_NOM = this.userForm.value.USR_NOM;
		user.USR_PRENOM = this.userForm.value.USR_PRENOM;
		user.USR_EMAIL = this.userForm.value.USR_EMAIL;
		user.USR_TEL_MOBILE = this.userForm.value.USR_TEL_MOBILE;
		user.USR_TYPE = UserTypeEnum.PARTICULIER;

		const token = JSON.parse(localStorage.getItem("token")!);
		if (token?.refId) user.REF_ID = token.refId;
		if (this.batiment?.simulationId) user.SIN_ID = parseInt(this.batiment.simulationId);

		return user;
	}

	private buildContactV2(): ContactV2 {
		return {
			firstname: this.userForm.value.USR_PRENOM,
			lastname: this.userForm.value.USR_NOM,
			email: this.userForm.value.USR_EMAIL,
			phoneNumber: this.userForm.value.USR_TEL_MOBILE || null
		};
	}

	private getSimUuid(): string {
		return JSON.parse(localStorage.getItem("simUuid")!) ?? '';
	}

	private openModalError(message: string) {
		this.app.confirmDialogService.confirmThis('', message, "alert", "", () => {
		}, () => {
		});
	}
}
